// Shared plumbing for the scoped, limited watchers in counter.ts and money.ts.
import {
  onSnapshot,
  type DocumentData,
  type DocumentReference,
  type Query,
} from 'firebase/firestore';

export type Unsubscribe = () => void;

function isTimestampLike(value: unknown): value is { toDate: () => Date } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as { toDate: unknown }).toDate === 'function'
  );
}

/** Firestore timestamps become ISO strings, so screens only ever see strings (like the schemas say). */
export function deepTimes<T>(value: T): T {
  if (isTimestampLike(value)) return value.toDate().toISOString() as unknown as T;
  if (Array.isArray(value)) return value.map((v) => deepTimes(v)) as unknown as T;
  if (typeof value === 'object' && value !== null) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = deepTimes(v);
    return out as T;
  }
  return value;
}

/** The document data with times as ISO strings and the id filled in under `idField`. */
export function toDoc<T>(id: string, data: DocumentData, idField?: string): T {
  const clean = deepTimes(data);
  return (idField && clean[idField] === undefined ? { ...clean, [idField]: id } : clean) as T;
}

/** Live query → mapped rows. Rows the mapper returns `null` for are skipped. */
export function watchRows<T>(
  q: Query,
  map: (id: string, data: DocumentData) => T | null,
  onData: (rows: T[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return onSnapshot(
    q,
    (snap) =>
      onData(
        snap.docs.flatMap((d) => {
          const row = map(d.id, d.data());
          return row === null ? [] : [row];
        }),
      ),
    onError,
  );
}

/** Live single document, or `null` when it does not exist. */
export function watchOne<T>(
  ref: DocumentReference,
  map: (id: string, data: DocumentData) => T,
  onData: (row: T | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return onSnapshot(
    ref,
    (snap) => onData(snap.exists() ? map(snap.id, snap.data()) : null),
    onError,
  );
}
